import router from '@src/components/app/Router';
import { MockModeProvider } from '@src/components/dev/MockModeProvider';
import { RewriteButtonProvider } from '@src/contexts/RewriteButtonContext';
import { getMockModeFromStorage } from '@src/modules/local_storage_utils';
import { preloadPdfiumLibrary } from '@src/modules/pdfium_wrapper';
import { errorHandler } from '@src/services/errorHandler';
import store from '@src/store/index';
import { ThemeProvider } from '@src/theme/ThemeProvider';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { RouterProvider } from 'react-router-dom';
import '@src/css/styles.css';
import '@src/i18n';

// グローバルエラーハンドラーの初期化
errorHandler; // シングルトンインスタンスの初期化

// PDFium WASMを事前初期化（初回PDF表示を高速化）
preloadPdfiumLibrary();

// アプリケーション起動時にモックモードの状態を初期化
if (import.meta.env.DEV) {
  try {
    // ローカルストレージからモックモードの状態を取得して初期化
    getMockModeFromStorage();
  } catch (_error) {
    // Mock mode initialization failed
  }
}

// TanStack Queryクライアントの最適化された設定
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // PDF生成は頻繁に変更されるため、短いstaleTime
      staleTime: 30 * 1000, // 30秒
      gcTime: 5 * 60 * 1000, // 5分
      retry: 1, // PDF生成は重いので1回のみリトライ
      retryDelay: 2000, // 2秒間隔
      refetchOnWindowFocus: false, // ウィンドウフォーカス時の再取得を無効化
      refetchOnReconnect: false, // ネットワーク再接続時の再取得を無効化
    },
    mutations: {
      retry: 1, // ミューテーションは1回のみリトライ
      retryDelay: 2000,
    },
  },
});

const renderApp = () => {
  const rootElement = document.getElementById('root');

  if (!rootElement) {
    console.error('Application root element with id "root" was not found.');
    return;
  }

  ReactDOM.createRoot(rootElement).render(
    <React.StrictMode>
      <Provider store={store}>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider>
            <MockModeProvider>
              <RewriteButtonProvider>
                <RouterProvider router={router} />
                <ReactQueryDevtools initialIsOpen={false} />
              </RewriteButtonProvider>
            </MockModeProvider>
          </ThemeProvider>
        </QueryClientProvider>
      </Provider>
    </React.StrictMode>
  );
};

if (document.readyState === 'loading') {
  window.addEventListener('DOMContentLoaded', renderApp, { once: true });
} else {
  renderApp();
}
